import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandsHoldingHeart } from '@fortawesome/pro-light-svg-icons/faHandsHoldingHeart';

import { Storage } from 'us-react-components/dist/constants/Storage';

import { Button, ButtonType } from '../../../components';
import { useDependencies } from '../../../dependencies';

interface Props {
  city: string;
  endDate: string;
  numberOfChildren: number;
  rate: number;
  startDate: string;
  state: string;
  street: string;
  zipCode: string;
}

const INFO_URL = '/app/family/agency-information';

// React-only sized variant of AgencySearchWidgetCard. The shared
// us-react-components version uses a fixed wider layout that the Angular
// search page depends on; this card keeps the same content but matches
// SitterCard's narrower footprint so it fits one grid cell on the React
// /sitters page without rebreaking Angular consumers.
export const AgencySitterCard = ({
  city,
  endDate,
  numberOfChildren,
  rate,
  startDate,
  state,
  street,
  zipCode,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const { services: { tracker } } = useDependencies();

  useEffect(() => {
    void tracker.track('viewed agency-search-card', { elementId: 'agency-search-card-sitter' });
  }, [tracker]);

  if (!rate || rate <= 0) return null;

  const onClick = async (): Promise<void> => {
    localStorage.setItem(Storage.AGENCY_CITY, city);
    localStorage.setItem(Storage.AGENCY_NUM_CHILDREN, String(numberOfChildren));
    localStorage.setItem(Storage.AGENCY_END_DATE, endDate);
    localStorage.setItem(Storage.AGENCY_START_DATE, startDate);
    localStorage.setItem(Storage.AGENCY_STREET, street);
    localStorage.setItem(Storage.AGENCY_STATE, state);
    localStorage.setItem(Storage.AGENCY_ZIP, zipCode);
    await tracker.track('clicked-button', { elementId: 'agency-search-card-sitter' });
    window.location.href = INFO_URL;
  };

  return (
    <div
      className='relative w-full bg-white border border-gray-300 rounded-lg p-4 shadow-sm hover:shadow-md cursor-pointer transition-shadow flex flex-col'
      onClick={onClick}
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void onClick();
        }
      }}
      role='link'
      tabIndex={0}
    >
      <div className='flex items-start gap-4 mb-3'>
        <FontAwesomeIcon
          icon={faHandsHoldingHeart}
          className='text-sapphire flex-none'
          style={{ fontSize: '48px' }}
        />
        <div className='flex-1 min-w-0'>
          <p className='text-lg font-semibold leading-tight m-0'>
            {t('components.agencySearchCard.title')}
          </p>
          <span className='text-base font-semibold'>${rate} /hr</span>
        </div>
      </div>
      <p className='text-sm font-semibold mb-1'>
        {t('components.agencySearchCard.subtitle')}
      </p>
      <span className='block text-sm leading-snug mb-4 text-neutral-700'>
        {t('components.agencySearchCard.details')}
      </span>
      <div className='mt-auto'>
        <Button buttonType={ButtonType.primary} className='w-full'>
          {t('components.agencySearchCard.button')}
        </Button>
      </div>
    </div>
  );
};
